import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { Switch } from '@/shared/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Separator } from '@/shared/components/ui/separator'
import { Badge } from '@/shared/components/ui/badge'
import { useCreateReporte, useTiposEnvio, useReportesDisponibles, useReportePorCampania } from '@/modules/cronograma/hooks/useReportes'
import { useToast } from '@/shared/hooks/use-toast'
import { Loader2, Plus, Trash2, Mail, Calendar, BarChart, FileText, Flag, FileSpreadsheet } from 'lucide-react'

// Schema de validación
const reporteSchema = z.object({
  campania_id: z.number(),
  // Métricas principales
  base: z.number().min(0, 'Debe ser mayor o igual a 0'),
  entrega: z.number().min(0, 'Debe ser mayor o igual a 0'),
  aperturas_unicas: z.number().min(0, 'Debe ser mayor o igual a 0'),
  aperturas_totales: z.number().min(0, 'Debe ser mayor o igual a 0'),
  clics_unicos: z.number().min(0, 'Debe ser mayor o igual a 0'),
  clics_totales: z.number().min(0, 'Debe ser mayor o igual a 0'),
  unsub: z.number().min(0, 'Debe ser mayor o igual a 0'),
  // Información general
  titulo_campana: z.string().optional().default(''),
  base_dirigida: z.string().optional().default(''),
  // Conclusión
  conclusion: z.string().optional().default(''),
  // Estado de recopilación
  completado: z.boolean(),
  dias_recopilacion: z.number().nullable().optional(),
  // Excel
  habilitar_boton_excel: z.boolean(),
  url_excel: z.string().url('URL inválida').optional().or(z.literal('')),
  // Redirección
  reporte_relacionado_id: z.number().optional(),
  // Asuntos
  asuntos: z
    .array(
      z.object({
        asunto: z.string().optional().default(''),
        preencabezado: z.string().optional(),
      })
    )
    .optional()
    .default([{ asunto: '', preencabezado: '' }]),
  // Cronogramas
  cronogramas: z
    .array(
      z.object({
        fecha: z.string().optional().default(''),
        hora: z.string().optional().default(''),
        tipo_envio_id: z.number().optional(),
      })
    )
    .optional()
    .default([{ fecha: '', hora: '' }]),
}).superRefine((data, ctx) => {
  // Si NO es redirección, validar campos requeridos
  if (!data.reporte_relacionado_id) {
    if (!data.titulo_campana || data.titulo_campana.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Título es requerido', path: ['titulo_campana'] })
    }
    if (!data.asuntos || data.asuntos.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Debe agregar al menos un asunto', path: ['asuntos'] })
    } else {
      data.asuntos.forEach((a, i) => {
        if (!a.asunto || a.asunto.trim() === '') {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Asunto es requerido', path: ['asuntos', i, 'asunto'] })
        }
      })
    }
    if (!data.cronogramas || data.cronogramas.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Debe agregar al menos un cronograma', path: ['cronogramas'] })
    } else {
      data.cronogramas.forEach((c, i) => {
        if (!c.fecha || c.fecha.trim() === '') {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Fecha es requerida', path: ['cronogramas', i, 'fecha'] })
        }
        if (!c.hora || c.hora.trim() === '') {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Hora es requerida', path: ['cronogramas', i, 'hora'] })
        }
      })
    }
  }
})

type ReporteFormValues = z.infer<typeof reporteSchema>

interface ReporteModalProps {
  open: boolean
  onClose: () => void
  campaniaId: number
  nombreCampania: string
  tieneReporte: boolean
}

export function ReporteModal({ open, onClose, campaniaId, nombreCampania, tieneReporte }: ReporteModalProps) {
  const { toast } = useToast()
  const createReporte = useCreateReporte()
  const { data: tiposEnvio = [] } = useTiposEnvio()
  const { data: reportesDisponibles = [] } = useReportesDisponibles()

  // Cargar reporte existente cuando el modal está abierto
  const { data: reporteExistente, isLoading: cargandoReporte, refetch } = useReportePorCampania(
    campaniaId,
    open // Cargar siempre que el modal esté abierto
  )

  // Refrescar datos cuando se abre el modal
  useEffect(() => {
    if (open && campaniaId) {
      refetch()
    }
  }, [open, campaniaId, refetch])

  const form = useForm<ReporteFormValues>({
    resolver: zodResolver(reporteSchema) as any,
    defaultValues: {
      campania_id: campaniaId,
      base: 0,
      entrega: 0,
      aperturas_unicas: 0,
      aperturas_totales: 0,
      clics_unicos: 0,
      clics_totales: 0,
      unsub: 0,
      titulo_campana: nombreCampania,
      base_dirigida: '',
      conclusion: '',
      completado: false,
      habilitar_boton_excel: false,
      url_excel: '',
      asuntos: [{ asunto: '', preencabezado: '' }],
      cronogramas: [{ fecha: '', hora: '', tipo_envio_id: undefined }],
    },
  })

  const {
    fields: asuntosFields,
    append: appendAsunto,
    remove: removeAsunto,
  } = useFieldArray({
    control: form.control,
    name: 'asuntos',
  })

  const {
    fields: cronogramasFields,
    append: appendCronograma,
    remove: removeCronograma,
  } = useFieldArray({
    control: form.control,
    name: 'cronogramas',
  })

  // Cargar datos del reporte existente cuando se obtienen
  useEffect(() => {
    if (reporteExistente && open) {
      form.reset({
        campania_id: campaniaId,
        base: reporteExistente.base || 0,
        entrega: reporteExistente.entrega || 0,
        aperturas_unicas: reporteExistente.aperturas_unicas || 0,
        aperturas_totales: reporteExistente.aperturas_totales || 0,
        clics_unicos: reporteExistente.clics_unicos || 0,
        clics_totales: reporteExistente.clics_totales || 0,
        unsub: reporteExistente.unsub || 0,
        titulo_campana: reporteExistente.titulo_campana || nombreCampania,
        base_dirigida: reporteExistente.base_dirigida || '',
        conclusion: reporteExistente.conclusion || '',
        completado: reporteExistente.completado || false,
        dias_recopilacion: reporteExistente.dias_recopilacion ?? undefined,
        habilitar_boton_excel: reporteExistente.habilitar_boton_excel || false,
        url_excel: reporteExistente.url_excel || '',
        reporte_relacionado_id: reporteExistente.reporte_relacionado_id ?? undefined,
        asuntos: reporteExistente.asuntos && reporteExistente.asuntos.length > 0
          ? reporteExistente.asuntos.map(a => ({ asunto: a.asunto || '', preencabezado: a.preencabezado || '' }))
          : [{ asunto: '', preencabezado: '' }],
        cronogramas: reporteExistente.cronogramas && reporteExistente.cronogramas.length > 0
          ? reporteExistente.cronogramas.map(c => {
              // El backend devuelve fecha_hora como datetime combinado, separar en fecha y hora
              let fecha = c.fecha || ''
              let hora = c.hora || ''
              if (c.fecha_hora) {
                const dt = new Date(c.fecha_hora)
                fecha = dt.toISOString().split('T')[0] // YYYY-MM-DD
                hora = dt.toTimeString().slice(0, 5)    // HH:MM
              }
              return { fecha, hora, tipo_envio_id: c.tipo_envio_id }
            })
          : [{ fecha: '', hora: '', tipo_envio_id: undefined }],
      })
    }
  }, [reporteExistente, open, campaniaId, nombreCampania, form])

  // Actualizar campania_id cuando cambia (solo si no hay reporte existente)
  useEffect(() => {
    if (!reporteExistente) {
      form.setValue('campania_id', campaniaId)
      form.setValue('titulo_campana', nombreCampania)
    }
  }, [campaniaId, nombreCampania, form, reporteExistente])

  // Calcular tasas en tiempo real
  const base = form.watch('base')
  const entrega = form.watch('entrega')
  const aperturas_unicas = form.watch('aperturas_unicas')
  const clics_unicos = form.watch('clics_unicos')
  const unsub = form.watch('unsub')
  const completado = form.watch('completado')
  const habilitarExcel = form.watch('habilitar_boton_excel')
  const reporteRelacionadoId = form.watch('reporte_relacionado_id')
  const esRedireccion = !!reporteRelacionadoId && reporteRelacionadoId > 0

  const tasa_entrega = base > 0 ? ((entrega / base) * 100).toFixed(2) : '0.00'
  const tasa_aperturas = entrega > 0 ? ((aperturas_unicas / entrega) * 100).toFixed(2) : '0.00'
  const tasa_clics = entrega > 0 ? ((clics_unicos / entrega) * 100).toFixed(2) : '0.00'
  const tasa_unsub = base > 0 ? ((unsub / base) * 100).toFixed(2) : '0.00'

  const onSubmit: SubmitHandler<ReporteFormValues> = async (data) => {
    try {
      let payload: any

      if (data.reporte_relacionado_id) {
        // Reporte de redirección: solo enviar datos mínimos
        payload = {
          campania_id: data.campania_id,
          reporte_relacionado_id: data.reporte_relacionado_id,
          titulo_campana: data.titulo_campana || nombreCampania,
          base: 0,
          entrega: 0,
          aperturas_unicas: 0,
          aperturas_totales: 0,
          clics_unicos: 0,
          clics_totales: 0,
          unsub: 0,
          completado: false,
          habilitar_boton_excel: false,
        }
      } else {
        // Reporte normal: enviar todos los datos
        payload = {
          ...data,
          cronogramas: (data.cronogramas || []).map((c, i) => ({
            fecha_hora: `${c.fecha}T${c.hora}:00`,
            tipo_envio_id: c.tipo_envio_id,
            orden: i + 1,
          })),
          asuntos: (data.asuntos || []).map((a, i) => ({
            ...a,
            orden: i + 1,
          })),
        }
      }
      await createReporte.mutateAsync(payload as any)
      toast({
        title: tieneReporte ? 'Reporte actualizado' : 'Reporte creado',
        description: tieneReporte
          ? 'El reporte de campaña se actualizó exitosamente'
          : 'El reporte de campaña se creó exitosamente',
      })
      onClose()
      form.reset()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo guardar el reporte. Intente nuevamente.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart className="h-5 w-5" />
            {tieneReporte ? 'Editar' : 'Crear'} Reporte de Campaña
          </DialogTitle>
          <DialogDescription>{nombreCampania}</DialogDescription>
        </DialogHeader>

        {cargandoReporte ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Cargando datos del reporte...</span>
          </div>
        ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Redirección */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-primary" />
                  {esRedireccion ? 'Redirección de Reporte' : 'Métricas Principales'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="reporte_relacionado_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Redirigir a Reporte</FormLabel>
                      <Select onValueChange={(value) => field.onChange(value && value !== '0' ? Number(value) : undefined)} value={field.value?.toString()}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No redirigir" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="0">No redirigir</SelectItem>
                          {reportesDisponibles.map((reporte) => (
                            <SelectItem key={reporte.id} value={reporte.id.toString()}>
                              {reporte.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {esRedireccion
                          ? 'Este reporte redirigirá al reporte seleccionado. No se requieren datos adicionales.'
                          : 'Seleccione un reporte existente para redirigir, o deje en "No redirigir" para crear un reporte completo.'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!esRedireccion && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-4 gap-4">
                      <FormField
                        control={form.control}
                        name="base"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Base (envíos)</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="entrega"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Entrega</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="aperturas_unicas"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Aperturas Únicas</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="aperturas_totales"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Aperturas Totales</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="clics_unicos"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Clics Únicos</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="clics_totales"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Clics Totales</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="unsub"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Desuscripciones</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Métricas Calculadas */}
                    <Separator />
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Entrega</p>
                        <p className="text-2xl font-bold text-green-600">{tasa_entrega}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Aperturas</p>
                        <p className="text-2xl font-bold text-blue-600">{tasa_aperturas}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Clics</p>
                        <p className="text-2xl font-bold text-amber-600">{tasa_clics}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-1">Tasa de Desuscripción</p>
                        <p className="text-2xl font-bold text-red-600">{tasa_unsub}%</p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {!esRedireccion && (
            <>
            {/* Información General */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Información General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="titulo_campana"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Título de la Campaña</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="base_dirigida"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Dirigida</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Asuntos Dinámicos */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Asuntos
                </CardTitle>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => appendAsunto({ asunto: '', preencabezado: '' })}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar Asunto
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {asuntosFields.map((field, index) => (
                  <div key={field.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary">Asunto #{index + 1}</Badge>
                      {asuntosFields.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeAsunto(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    <FormField
                      control={form.control}
                      name={`asuntos.${index}.asunto`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input {...field} placeholder="Línea de asunto" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`asuntos.${index}.preencabezado`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input {...field} placeholder="Preencabezado (texto de vista previa)" maxLength={200} />
                          </FormControl>
                          <FormDescription>Texto que aparece junto al asunto en la bandeja de entrada</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Cronogramas Dinámicos */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Cronogramas
                </CardTitle>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => appendCronograma({ fecha: '', hora: '', tipo_envio_id: undefined })}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar Cronograma
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {cronogramasFields.map((field, index) => (
                  <div key={field.id} className="flex gap-3 items-start">
                    <FormField
                      control={form.control}
                      name={`cronogramas.${index}.fecha`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`cronogramas.${index}.hora`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`cronogramas.${index}.tipo_envio_id`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <Select onValueChange={(value) => field.onChange(Number(value))} value={field.value?.toString()}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Tipo de envío" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {tiposEnvio.map((tipo) => (
                                <SelectItem key={tipo.id} value={tipo.id.toString()}>
                                  {tipo.nombre}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {cronogramasFields.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeCronograma(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Conclusión */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Conclusión
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="conclusion"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Conclusión</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Estado de Recopilación */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flag className="h-5 w-5 text-primary" />
                  Estado de Recopilación
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel>Recopilación finalizada</FormLabel>
                    <FormDescription>Marcar cuando se deje de recopilar información</FormDescription>
                  </div>
                  <FormField
                    control={form.control}
                    name="completado"
                    render={({ field }) => (
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    )}
                  />
                </div>
                {completado && (
                  <FormField
                    control={form.control}
                    name="dias_recopilacion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Días de recopilación</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)} placeholder="Ej: 15" />
                        </FormControl>
                        <FormDescription>Días desde el lanzamiento</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            {/* Botón Excel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-green-600">
                  <FileSpreadsheet className="h-5 w-5" />
                  Exportar a Excel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel>Habilitar botón de Excel en reporte</FormLabel>
                    <FormDescription>Muestra un botón de descarga de Excel en el reporte</FormDescription>
                  </div>
                  <FormField
                    control={form.control}
                    name="habilitar_boton_excel"
                    render={({ field }) => (
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    )}
                  />
                </div>
                {habilitarExcel && (
                  <FormField
                    control={form.control}
                    name="url_excel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL del archivo Excel</FormLabel>
                        <FormControl>
                          <Input type="url" {...field} placeholder="https://ejemplo.com/archivo.xlsx" />
                        </FormControl>
                        <FormDescription>URL del archivo Excel para descarga</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
            </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={createReporte.isPending}>
                {createReporte.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {esRedireccion
                  ? 'Guardar Redirección'
                  : tieneReporte ? 'Actualizar Reporte' : 'Guardar Reporte'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
